import { SortOrder } from "antd-v5/lib/table/interface";
import { isEmpty } from "lodash";
import { useCallback, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  FilterChangeCallBack,
  FilterInfo,
  FilterOperationInfo,
  GroupByChangeCallback,
  IsVisualModeChangeCallback,
  PageChangeCallBack,
  PaginationControllerParams,
  PaginationControls,
  ResetFiltersCallback,
  SavedSearchInfo,
  SortChangeCallBack,
  SortInfo,
  UrlPagination,
} from "types/pagination";

import useUrlFilters from "./useUrlFilters";

export default function usePaginationUrlControls({
  defaultPageSize = 10,
}: PaginationControllerParams = {}): PaginationControls {
  const history = useHistory();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const paginationParam = urlParams.get("pagination") || "{}";
  const groupByParam = urlParams.get("groupBy");
  const savedSearchParam = urlParams.get("savedSearch");
  const filterOperationParam = urlParams.get("filterOperations");
  const isVisualModeParam = urlParams.get("isVisualMode");
  const pagination = useMemo<UrlPagination>(() => {
    if (isEmpty(paginationParam)) {
      return {};
    }
    return JSON.parse(paginationParam);
  }, [paginationParam]);

  const page = pagination.page || 1;
  const pageSize = pagination.size || defaultPageSize;
  const filterInfo = useUrlFilters() as FilterInfo;
  const sortString = pagination.sort || "";
  const savedSearchInfo = useMemo(
    () =>
      savedSearchParam
        ? (JSON.parse(savedSearchParam) as SavedSearchInfo)
        : undefined,
    [savedSearchParam],
  );

  const groupBy = useMemo(
    () => (groupByParam ? (JSON.parse(groupByParam) as string[]) : undefined),
    [groupByParam],
  );
  const filterOperations = useMemo(
    () =>
      filterOperationParam
        ? (JSON.parse(filterOperationParam) as FilterOperationInfo)
        : {},
    [filterOperationParam],
  );

  // Parse isVisualMode - undefined for legacy views (use existing parsing logic)
  const isVisualMode =
    isVisualModeParam === "true"
      ? true
      : isVisualModeParam === "false"
        ? false
        : undefined;

  const sortInfo = useMemo(() => {
    const sortInfo: SortInfo = {};
    if (isEmpty(sortString)) {
      return sortInfo;
    }
    sortString.split(":").forEach((sort) => {
      const [key, order] = sort.split(",");
      sortInfo[key] = order as SortOrder;
    });
    return sortInfo;
  }, [sortString]);

  const onPageChange: PageChangeCallBack = useCallback(
    (page, pageSize) => {
      const searchParams = getUrlParams({
        page,
        pageSize,
        sortInfo,
        filterInfo,
        defaultPageSize,
        savedSearchInfo,
        groupBy,
        filterOperations,
        isVisualMode,
      });
      history.replace(`${location.pathname}?${searchParams}`);
    },
    [
      sortInfo,
      filterInfo,
      defaultPageSize,
      history,
      location.pathname,
      savedSearchInfo,
      groupBy,
      filterOperations,
      isVisualMode,
    ],
  );

  const onSortChange: SortChangeCallBack = useCallback(
    (sortInfo) => {
      const searchParams = getUrlParams({
        page,
        pageSize,
        sortInfo,
        filterInfo,
        defaultPageSize,
        savedSearchInfo,
        groupBy,
        filterOperations,
        isVisualMode,
      });
      history.replace(`${location.pathname}?${searchParams}`);
    },
    [
      filterInfo,
      defaultPageSize,
      history,
      location.pathname,
      page,
      pageSize,
      savedSearchInfo,
      groupBy,
      filterOperations,
      isVisualMode,
    ],
  );

  const onFilterChange: FilterChangeCallBack = useCallback(
    (filterInfo, filterOperations) => {
      const searchParams = getUrlParams({
        page: 1,
        pageSize,
        sortInfo,
        filterInfo,
        defaultPageSize,
        savedSearchInfo,
        groupBy,
        filterOperations,
        isVisualMode,
      });
      history.replace(`${location.pathname}?${searchParams}`);
    },
    [
      defaultPageSize,
      history,
      location.pathname,
      sortInfo,
      pageSize,
      savedSearchInfo,
      groupBy,
      isVisualMode,
    ],
  );

  const resetFilters: ResetFiltersCallback = useCallback(
    ({
      filters: initialFilters,
      groupBy: initialGroupBy,
      savedSearch: initialSavedSearch,
      sortInfo: initialSortInfo = {},
      pageSize: initialPageSize,
      sortString: initialSortString,
      isVisualMode: initialIsVisualMode,
    } = {}) => {
      const searchParams = getUrlParams({
        page: 1,
        pageSize: initialPageSize || defaultPageSize,
        sortInfo: initialSortInfo,
        filterInfo: initialFilters || {},
        defaultPageSize,
        savedSearchInfo: initialSavedSearch,
        groupBy: initialGroupBy,
        sortString: initialSortString,
        isVisualMode: initialIsVisualMode,
      });
      const oldSearchParams = location.search;
      history.replace(`${location.pathname}?${searchParams}`);
      return oldSearchParams !== searchParams;
    },
    [defaultPageSize, history, location.pathname, location.search],
  );

  const onGroupByChange: GroupByChangeCallback = useCallback(
    (groupBy) => {
      const searchParams = getUrlParams({
        page: 1,
        pageSize: defaultPageSize,
        sortInfo,
        filterInfo,
        defaultPageSize,
        savedSearchInfo,
        groupBy,
        filterOperations,
        isVisualMode,
      });
      history.replace(`${location.pathname}?${searchParams}`);
    },
    [
      defaultPageSize,
      history,
      sortInfo,
      location.pathname,
      savedSearchInfo,
      filterInfo,
      filterOperations,
      isVisualMode,
    ],
  );

  const setIsVisualMode: IsVisualModeChangeCallback = useCallback(
    (newIsVisualMode) => {
      const searchParams = getUrlParams({
        page,
        pageSize,
        sortInfo,
        filterInfo,
        defaultPageSize,
        savedSearchInfo,
        groupBy,
        filterOperations,
        isVisualMode: newIsVisualMode,
      });
      history.replace(`${location.pathname}?${searchParams}`);
    },
    [
      page,
      pageSize,
      sortInfo,
      filterInfo,
      defaultPageSize,
      history,
      location.pathname,
      savedSearchInfo,
      groupBy,
      filterOperations,
    ],
  );

  return useMemo(
    () => ({
      page,
      pageSize,
      sortInfo,
      sortString,
      filterInfo,
      filterOperations,
      onPageChange,
      onSortChange,
      onFilterChange,
      resetFilters,
      savedSearchInfo,
      groupBy,
      onGroupByChange,
      defaultPageSize,
      isVisualMode,
      setIsVisualMode,
    }),
    [
      page,
      pageSize,
      sortInfo,
      sortString,
      filterInfo,
      filterOperations,
      onPageChange,
      onSortChange,
      onFilterChange,
      resetFilters,
      savedSearchInfo,
      groupBy,
      onGroupByChange,
      defaultPageSize,
      isVisualMode,
      setIsVisualMode,
    ],
  );
}

export const getSortString = (sortInfo: SortInfo): string => {
  return Object.keys(sortInfo)
    .map((key) => {
      return `${key},${sortInfo[key]}`;
    })
    .join(":");
};

const getUrlParams = ({
  page,
  pageSize,
  defaultPageSize,
  sortInfo,
  filterInfo,
  savedSearchInfo,
  groupBy,
  sortString: _sortString,
  filterOperations,
  isVisualMode,
}: {
  page: number;
  pageSize: number;
  defaultPageSize?: number;
  sortInfo: SortInfo;
  filterInfo: FilterInfo;
  savedSearchInfo: SavedSearchInfo;
  groupBy?: string[];
  sortString?: string;
  filterOperations?: FilterOperationInfo;
  isVisualMode?: boolean;
}) => {
  const pagination: UrlPagination = {};

  if (page > 1) {
    pagination.page = page;
  }
  if (pageSize && pageSize !== defaultPageSize) {
    pagination.size = pageSize;
  }
  const sortString = _sortString || getSortString(sortInfo);
  if (sortString) {
    pagination.sort = sortString;
  }
  const params = new URLSearchParams();
  if (!isEmpty(pagination)) {
    params.set("pagination", JSON.stringify(pagination));
  }

  if (!isEmpty(filterInfo)) {
    params.set("filters", JSON.stringify(filterInfo));
  }
  if (!isEmpty(savedSearchInfo)) {
    params.set("savedSearch", JSON.stringify(savedSearchInfo));
  }
  if (!isEmpty(groupBy)) {
    params.set("groupBy", JSON.stringify(groupBy));
  }
  if (!isEmpty(filterOperations)) {
    params.set("filterOperations", JSON.stringify(filterOperations));
  }
  // Handle isVisualMode - only set when explicitly provided
  if (isVisualMode !== undefined) {
    params.set("isVisualMode", String(isVisualMode));
  }

  return params.toString();
};
